// @ts-strict-ignore
import {
  Component, EventEmitter, Input,
  Output, ViewChild
} from '@angular/core';
import {Observable, takeUntil, throwError} from 'rxjs';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { catchError, map } from 'rxjs/operators';
import { LMContextService, LMLoggerService } from '@app/services';
import { LMModel } from '@form/model';
import {
  LMGenAIPromptGeneratedEvent,
  LMGenAIPromptMessageObject,
  LMGenAIPromptResponse
} from '@modal/lm-genai-prompt-dialog';
import {
  SohoAlertDirective,
  SohoButtonComponent,
  SohoEditorComponent,
} from 'ids-enterprise-ng';
import DOMPurify from 'dompurify';

@Component({
  selector:     'lm-genai-prompt',
  standalone: false,
  templateUrl:  'lm-genai-prompt-dialog.component.html',
  styleUrls:    [ 'lm-genai-prompt-dialog.component.scss' ],
})

export class LMGenAIPromptDialogComponent {
  constructor(private http: HttpClient,
    private lmContextService: LMContextService,
    private lmLoggerService: LMLoggerService,) {}

  @Input() generatedText: string;
  @Input() model: LMModel;
  @Input() fieldId: string;
  @Input() dataView: LMDataView;
  @Input() url: string;
  @Input() isRephraseMode: boolean;
  @Output() generatedTextEvent = new EventEmitter<LMGenAIPromptGeneratedEvent>();
  @ViewChild(SohoEditorComponent) generatedField: SohoEditorComponent;
  @ViewChild('generateButton') public generateButton: SohoButtonComponent;
  @ViewChild(SohoAlertDirective) alert: SohoAlertDirective;

  public isGenerating = false;
  public isGeneratedError = false;
  public generatedErrorMessage = '';
  public disclaimerMessage = Soho.Locale.translate('GeneratedAIDisclaimerMessage');
  public dialogTitle = Soho.Locale.translate('KeyPointsLabel');

  public displayPopupMenu = false;
  private _contextMenuEvent: any;

  public genAIPrompt = {
    value: '',
    changeLength: null,
    changeTone: null,
  };

  public genAIResponse = {
    value: ''
  };

  public get contextMenuEvent(): any {
    return this._contextMenuEvent;
  }

  public getGeneratedText() {
    return this.genAIResponse.value;
  }

  public getDataView() {
    return this.dataView;
  }

  onInit() {
    this.isRephraseMode = true;
    if (this.isRephraseMode) {
      this.dialogTitle = 'Rephrase Text';
    }
  }

  onGenerateClickEvent(event: Event) {
    const promptValue = DOMPurify.sanitize(this.genAIPrompt.value);

    if (promptValue.length <= 0) {
      return;
    }

    this.isGenerating = true;
    this.genAIResponse.value = '';
    this.generatedField.writeValue(this.genAIResponse.value);

    this.generateAssistedText()
      .subscribe((response: LMGenAIPromptResponse) => {
        if (response.status === 'VALID') {
          (this.generatedField as any).editor.getCurrentElement().find('.ai-loading-indicator').remove();
          this.isGeneratedError = false;

          this.generatedErrorMessage = '';
          this.genAIResponse.value = DOMPurify.sanitize(response.text, {FORBID_TAGS: ['style', 'form', 'button', 'a', 'input', 'select', 'textarea']});
          this.generatedField.writeValue(this.genAIResponse.value);

          const lmGeneratedTextEvent: LMGenAIPromptGeneratedEvent = <LMGenAIPromptGeneratedEvent>{ valid: true };
          this.generatedTextEvent.emit(lmGeneratedTextEvent);
        } else if (response.error) {
          (this.generatedField as any).editor.getCurrentElement().find('.ai-loading-indicator').remove();
          this.isGeneratedError = true;

          if  (typeof response.error.message === 'object') {
            const error: LMGenAIPromptMessageObject = response.error.message;
            if (typeof error.message === 'string') {
              this.generatedErrorMessage = error.message;
            }
          } else if (typeof response.error.message === 'string') {
            this.generatedErrorMessage = response.error.message;
          } else {
            this.generatedErrorMessage = '';
          }

          this.genAIResponse.value = '';
          this.generatedField.writeValue(this.genAIResponse.value);

          const lmGeneratedTextEvent: LMGenAIPromptGeneratedEvent = <LMGenAIPromptGeneratedEvent>{ valid: false };
          this.generatedTextEvent.emit(lmGeneratedTextEvent);
        }
        this.isGenerating = false;
      });
  }

  generateAssistedText(): Observable<LMGenAIPromptResponse> {
    const loadingIndicator = $('<div class="ai-loading-indicator"><div class="bar"></div><div class="bar"></div><div class="bar"></div></div>');

    const currentElement = (this.generatedField as any).editor.getCurrentElement();
    loadingIndicator.appendTo(currentElement);

    const formRequest = {
      requestType: 'genAIPrompt',
      prompt: this.isRephraseMode? this.buildRephrasePrompt() : this.genAIPrompt.value,
    };

    const headers = new HttpHeaders();
    headers.append('Content-Type', 'application/json;charset=utf-8');

    return this.http.post<LMGenAIPromptResponse>(this.url, JSON.stringify(formRequest), { headers }).pipe(
      map((response: LMGenAIPromptResponse) => {
        return response;
      }),
      catchError(error => this._handleError(error)));
  }

  private _handleError(error: Response) {
    // in a real world app, we may send the error to some remote logging infrastructure
    // instead of just logging it to the console
    this.lmLoggerService.log(error);
    return throwError(error || 'Server error');
  }

  isModified(): boolean {
    return this.genAIPrompt.value.length > 0 || this.genAIResponse.value.length > 0;
  }

  private buildRephrasePrompt(): string {
    let prompt;
    if (this.genAIPrompt.changeLength && this.genAIPrompt.changeTone) {
      prompt = this.genAIPrompt.changeLength + ' the following text slightly, changing the tone to ' + this.genAIPrompt.changeTone;
    } else if (this.genAIPrompt.changeLength) {
      prompt = this.genAIPrompt.changeLength + ' the following text slightly';
    } else if (this.genAIPrompt.changeTone) {
      prompt = 'Rephrase the following text, changing the tone to ' + this.genAIPrompt.changeTone;
    } else {
      prompt = 'Rephrase the following text';
    }
    return prompt + ': ' + this.genAIPrompt.value;
  }
}
